import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaStore, FaBan } from 'react-icons/fa';
import { Page, PageHeader, Card, Badge, Alert, Loading, bookingStatus, formatMoney, formatDate } from '../../ui';
import { ServiceSummary, CustomerCard, VendorHistory, Findings } from '../../ui/JobParts';
import { api } from '../../ui/api';
import VendorPicker from '../Audits/VendorPicker';

const ManageBooking = () => {
  const { booking_id } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api(`/booking/get_booking/${booking_id}`).then(setBooking).catch((e) => setError(e.message));
  }, [booking_id]);
  useEffect(() => { load(); }, [load]);

  if (error && !booking) return <Page><Alert>{error}</Alert></Page>;
  if (!booking) return <Page><Loading /></Page>;

  const status = bookingStatus(booking.status_code);
  const assignments = booking.vendor_assignment_list || [];
  const currentVendor = assignments.length ? assignments[assignments.length - 1].vendor : null;
  const code = booking.status_code;
  const canAssign = code === 0;
  const canChange = code > 0 && code < 4;
  const canCancel = code >= 0 && code < 4;
  const audit = booking.audit;
  const quote = audit && audit.final_price ? audit.final_price.final_price : null;
  const scope = audit && audit.audit_details && audit.audit_details.final_audit_details;
  const work = booking.booking_details && booking.booking_details.final_booking_details;

  const assign = async (vendor) => {
    const path = canAssign ? 'assign_vendor' : 'change_vendor';
    await api(`/booking/${path}/${booking_id}`, { method: 'POST', body: { vendor_id: vendor.id } });
    load();
  };

  const cancel = async () => {
    if (!window.confirm('Cancel this booking? The customer will see it as cancelled.')) return;
    setBusy(true);
    try {
      await api(`/booking/cancel_booking/${booking_id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title={`Booking ${booking.booking_id}`}
        subtitle={`Created ${formatDate(booking.date_created)}`}
        back={{ to: '/bookings', label: 'Bookings' }}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />
      <Alert>{error}</Alert>

      <div className="grid grid-sidebar">
        <div className="stack">
          <ServiceSummary service={booking.service} date={booking.date} time={booking.time} address={booking.address} note={booking.comment} />
          {Array.isArray(work) && work.length > 0 && typeof work[0] === 'object'
            ? <Findings title="Work completed" items={work} remarkKey="newRemark" onlySelected />
            : <Findings title="Agreed scope" items={scope} onlySelected />}
          <VendorHistory assignments={assignments} />
        </div>

        <div className="stack sticky-side side-first">
          {(canAssign || canChange || canCancel) && (
            <Card title="Next step">
              <div className="action-list">
                {canAssign && <button className="btn btn-primary" onClick={() => setPicking(true)}><FaStore /> Assign vendor</button>}
                {canChange && <button className="btn btn-secondary" onClick={() => setPicking(true)}><FaStore /> Change vendor</button>}
                {canCancel && <button className="btn btn-danger-outline" disabled={busy} onClick={cancel}><FaBan /> Cancel booking</button>}
              </div>
            </Card>
          )}
          {quote !== null && quote !== undefined && (
            <Card title="Agreed price">
              <div className="quote">
                <span className="price">{formatMoney(quote)}</span>
                {audit && <Link to={`/audits/manage-audit/${audit.id}`} className="small">From audit {audit.audit_id}</Link>}
              </div>
            </Card>
          )}
          {booking.review && booking.review.rating && (
            <Card title="Customer review">
              <div className="stars" aria-label={`${booking.review.rating} out of 5`}>
                {'★★★★★'.slice(0, booking.review.rating)}<span>{'★★★★★'.slice(booking.review.rating)}</span>
              </div>
              {booking.review.text && <p style={{ marginTop: 8 }}>“{booking.review.text}”</p>}
            </Card>
          )}
          <CustomerCard user={booking.user} />
        </div>
      </div>

      <VendorPicker
        open={picking}
        title={canAssign ? 'Assign a vendor' : 'Change vendor'}
        currentVendorId={canChange && currentVendor ? currentVendor.id : null}
        onClose={() => setPicking(false)}
        onPick={assign}
      />
    </Page>
  );
};

export default ManageBooking;
